import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'

export interface CartItem {
  id: string
  name: string
  price: number
  quantity: number
  image: string
  description?: string
}

const CART_STORAGE_KEY = 'supplemearth_cart'

// Función para cargar el carrito desde localStorage
function loadCartFromStorage(): CartItem[] {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch { /* ignore */ }
  return []
}

// Función para guardar el carrito en localStorage
function saveCartToStorage(items: CartItem[]) {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items))
  } catch { /* ignore */ }
}

export const useCartStore = defineStore('cart', () => {
  // State - Cargar desde localStorage al inicializar
  const items = ref<CartItem[]>(loadCartFromStorage())
  const isCartOpen = ref(false)
  
  // Sincronizar con localStorage cada vez que cambien los items
  watch(
    items,
    (newItems) => {
      saveCartToStorage(newItems)
    },
    { deep: true }
  )

  // Getters
  const itemCount = computed(() => {
    return items.value.reduce((total, item) => total + item.quantity, 0)
  })

  const totalPrice = computed(() => {
    return items.value.reduce((total, item) => total + (item.price * item.quantity), 0)
  })

  const isEmpty = computed(() => items.value.length === 0)

  // Actions
  function addItem(product: Omit<CartItem, 'quantity'>) {
    const existingItem = items.value.find(item => item.id === product.id)
    
    if (existingItem) {
      existingItem.quantity++
    } else {
      items.value.push({ ...product, quantity: 1 })
    }
  }

  function removeItem(productId: string) {
    const index = items.value.findIndex(item => item.id === productId)
    if (index > -1) {
      items.value.splice(index, 1)
    }
  }

  function updateQuantity(productId: string, quantity: number) {
    const item = items.value.find(item => item.id === productId)
    if (item) {
      item.quantity = quantity
      if (item.quantity <= 0) {
        removeItem(productId)
      }
    }
  }

  function clearCart() {
    items.value = []
    // También limpiar localStorage
    localStorage.removeItem(CART_STORAGE_KEY)
  }
  
  // Función para inicializar el carrito desde localStorage (útil si se necesita recargar manualmente)
  function initCart() {
    items.value = loadCartFromStorage()
  }

  function toggleCart() {
    isCartOpen.value = !isCartOpen.value
  }

  function openCart() {
    isCartOpen.value = true
  }

  function closeCart() {
    isCartOpen.value = false
  }

  return {
    items,
    isCartOpen,
    itemCount,
    totalPrice,
    isEmpty,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    initCart,
    toggleCart,
    openCart,
    closeCart,
  }
})
